/*
 * File: PostChargesForm.js
 * Project: mp-mobile-app
 *
 * Created by Jennifer Loecker on 10/17/2019
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: The main New Charge page
 */
import React from "react";
import AddIconBox from "./AddIconBox";
import PostChargeUIDialog from "./PostChargeUIDialog";
import DiagnosisCard from "./diagnosis/DiagnosisCard";
import UISearchStore from "../store/UISearchStore";
import SelectedDiagnosesStore from "../store/SelectedDiagnosesStore";
import ProceduresTable from "./procedure/ProceduresTable";
import { Card, FormControl } from "@material-ui/core";
import PostChargesModel from "../store/PostChargesModel";
import UIStore from "../../store/UIStore";
import { observer } from "mobx-react";
import { withStyles } from "@material-ui/core/styles";
import ProviderSelectField from "./ProviderSelectField";
import ProviderOfficeFeeModel from "services/patients/demographics/provider_office_fee/store/ProviderOfficeFeeModel";
import UIProgress from "components/ui/UIProgress";
import ProblemAPI from "services/patients/clinical/problemList/Api";
import PatientIdStorage from "../../store/PatientIdStore";
import CreateOrderButton from "./CreateOrderButton";
import PropTypes from "prop-types";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { toDate } from "date-fns";
import { StoreContext } from "utils/useStore";

const styles = theme => ({
  card: {
    marginBottom: theme.spacing(2),
    position: "relative"
  },
  formControl: {
    marginTop: theme.spacing(1.5)
  }
});

class PostChargesForm extends React.Component {
  state = {
    errorMessage: "",
    loading: true
  };

  static contextType = StoreContext;
  constructor(props) {
    super(props);
    this.uiDiagnosisDialogStore = UIStore.create();
    this.uiProcedureCodeDialogStore = UIStore.create();
    this.postChargeModel = PostChargesModel.create();
    this.editingChargeModel = null;
  }

  get uiSearchStore() {
    return this.context.getStore(UISearchStore);
  }

  get selectedDiagnosesStore() {
    return this.context.getStore(SelectedDiagnosesStore);
  }

  async componentDidMount() {
    let patientId = PatientIdStorage.getId();
    let problems = await ProblemAPI.getProblems(patientId);
    this.providerOfficeFeeModel = this.context.getStore(ProviderOfficeFeeModel);
    await this.providerOfficeFeeModel.getProviderOfficeFeeData();
    this.postChargeModel.setProviderId(
      this.providerOfficeFeeModel.patientProvider.userId
    );
    this.uiSearchStore.setFeeScheduleId(
      this.providerOfficeFeeModel.feeSchedule.id
    );
    this.selectedDiagnosesStore.addPatientProblemsToDiagnosesList(problems);
    this.setState({ loading: false });
  }

  procedureDialogCallBack = () => {
    // set editingChargeModel to null to avoid pulling up procedure editing form when trying to add a procedure
    this.editingChargeModel = null;
    this.uiProcedureCodeDialogStore.toggleFormDialog();
    this.editingChargeModel = null;
  };

  editFormSaveChanges = copyOfModel => {
    this.postChargeModel.editCharge(this.editingChargeModel, copyOfModel);
    this.procedureDialogCallBack();
  };

  editCharge = row => {
    this.editingChargeModel = row;
    this.uiProcedureCodeDialogStore.toggleFormDialog();
  };

  changeProcedureDate = date => {
    let newDate = toDate(date);
    this.postChargeModel.changeField("procedureDate", newDate);
  };

  checkForError = () => {
    let errorMessage = this.postChargeModel.validate();
    this.setState({ errorMessage: errorMessage });
    return errorMessage === "";
  };

  render() {
    const { loading } = this.state;
    const { classes } = this.props;

    if (loading) return <UIProgress />;
    if (this.uiProcedureCodeDialogStore.showFormDialog) {
      return (
        <PostChargeUIDialog
          parentCallBack={this.procedureDialogCallBack}
          title="Procedures"
          uiSearchStore={this.uiSearchStore}
          postChargesModel={this.postChargeModel}
          selectedDiagnosesStore={this.selectedDiagnosesStore}
          editingChargeModel={this.editingChargeModel}
          editFormSaveChanges={this.editFormSaveChanges}
          feeScheduleName={this.providerOfficeFeeModel.feeScheduleName}
        />
      );
    } else {
      return (
        <div className="padding">
          <FormControl fullWidth={true}>
            <KeyboardDatePicker
              autoOk
              fullWidth
              value={this.postChargeModel.procedureDate}
              inputVariant="filled"
              label="Procedure Date"
              format="MM/dd/yyyy"
              onChange={this.changeProcedureDate}
            />

            <FormControl fullWidth={true} className={classes.formControl}>
              <ProviderSelectField
                chargeModel={this.postChargeModel}
                errorMessage={this.state.errorMessage}
                checkForError={this.checkForError}
              />
            </FormControl>
            <DiagnosisCard
              selectedDiagnosesStore={this.selectedDiagnosesStore}
              postChargeModel={this.postChargeModel}
            />
            <div className={classes.card}>
              <Card>
                <AddIconBox
                  label="Procedures"
                  addIconOnClick={
                    this.uiProcedureCodeDialogStore.toggleFormDialog
                  }
                />
                {this.postChargeModel.hasCharges && (
                  <ProceduresTable
                    postChargeModel={this.postChargeModel}
                    selectedDiagnosisStore={this.selectedDiagnosesStore}
                    editCharge={this.editCharge}
                  />
                )}
              </Card>
            </div>
            <CreateOrderButton
              postChargeModel={this.postChargeModel}
              procedureDate={this.procedureDate}
              history={this.props.history}
              checkForError={this.checkForError}
            />
          </FormControl>
        </div>
      );
    }
  }
}

PostChargesForm.propTypes = {
  classes: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired
};

export default withStyles(styles)(observer(PostChargesForm));
